import React from 'react';
import { Toolbar, SaveButton, Button, useNotify, useTranslate } from 'react-admin';
import { useDataProvider } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Button as MuiButton, Stack } from '@mui/material';
import { transformEventData, ensureStockTicketIds } from '../../../utils/eventFormUtils';
import { useEventTabNav } from './EventTabNav';

const newEventLayoutId = () =>
  `el_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

/**
 * Event Form Toolbar Component
 * Create: Back / Next through tabs (no URL change), Save on last tab
 * Edit: Save only
 */
export const EventFormToolbar = ({ isEdit = false, eventType, dateType }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { getValues } = useFormContext();
  const { currentTab, tabCount, goToTab } = useEventTabNav();

  const isLastTab = currentTab >= tabCount - 1;
  const isFirstTab = currentTab <= 0;

  const handleSave = async (data) => {
    const allValues = getValues();
    // SaveButton bazen Bloblari / Controller alanlarini dusurur; getValues ile birlestir.
    const pickMedia = (key) => {
      const fromData = data?.[key];
      const fromForm = allValues?.[key];
      if (fromData?.rawFile) return fromData;
      if (fromForm?.rawFile) return fromForm;
      return fromData !== undefined ? fromData : fromForm;
    };

    const payload = {
      ...allValues,
      ...data,
      banner: pickMedia('banner'),
      bannerCard: pickMedia('bannerCard'),
      venueLayout: pickMedia('venueLayout'),
    };

    // Form submit bazen layoutJSON'u kaciriyor; getValues ile garanti et.
    let layoutJSON = payload.layoutJSON ?? allValues.layoutJSON;
    delete payload.layoutJSON;

    const assertImageUploadable = (field, label) => {
      const img = payload[field];
      if (img?.src && String(img.src).startsWith('blob:') && !img.rawFile) {
        notify(`${label} yüklemesi bozuldu — Visuals sekmesinde görseli tekrar seçip Save edin`, {
          type: 'error',
        });
        throw new Error(`${field} missing rawFile`);
      }
    };
    assertImageUploadable('banner', '2. tasarım görseli');
    assertImageUploadable('bannerCard', '1. tasarım görseli');

    if (payload.layoutID) {
      // Hic yoksa base layout'tan al (en azindan koltuk plani kaydolsun)
      if (layoutJSON == null || layoutJSON === '') {
        const existingId = payload.layoutJSONID || allValues.layoutJSONID;
        if (isEdit && existingId) {
          try {
            const { data: existing } = await dataProvider.getOne('eventLayouts', {
              id: existingId,
            });
            layoutJSON = existing?.layoutJSON ?? null;
          } catch (e) {
            console.error('Existing eventLayout fetch failed', e);
          }
        }
      }
      if (layoutJSON == null || layoutJSON === '') {
        try {
          const { data: layout } = await dataProvider.getOne('layouts', { id: payload.layoutID });
          layoutJSON = layout?.layout ?? null;
        } catch (e) {
          console.error('Base layout fetch failed', e);
        }
      }

      if (layoutJSON != null && layoutJSON !== '') {
        const eventLayoutId =
          payload.layoutJSONID ||
          allValues.layoutJSONID ||
          (isEdit && payload.id) ||
          newEventLayoutId();
        payload.layoutJSONID = eventLayoutId;

        try {
          await dataProvider.update('eventLayouts', {
            id: eventLayoutId,
            data: {
              layoutJSON,
              event: isEdit && payload.id ? payload.id : null,
            },
          });
        } catch (e) {
          console.error('eventLayouts save failed', e);
          notify(translate('form.layout_save_failed', { error: e?.message || e }), {
            type: 'error',
          });
          throw e;
        }
      } else {
        notify(translate('form.layout_empty_warning'), {
          type: 'warning',
        });
      }
    }

    const stockRecord = payload._stockRecord;
    if (isEdit && stockRecord?.id && stockRecord.stock != null) {
      await dataProvider.update('stocks', {
        id: stockRecord.id,
        data: {
          stock: ensureStockTicketIds(stockRecord.stock),
        },
      });
    }
    delete payload._stockRecord;

    return transformEventData(payload, eventType, dateType);
  };

  return (
    <Toolbar sx={{ justifyContent: 'flex-start', gap: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {!isEdit && (
          <MuiButton
            variant="outlined"
            disabled={isFirstTab}
            onClick={() => goToTab(currentTab - 1)}
          >
            {translate('create_wizard.back', 'Back')}
          </MuiButton>
        )}

        {!isEdit && !isLastTab && (
          <MuiButton variant="contained" onClick={() => goToTab(currentTab + 1)}>
            {translate('create_wizard.next', 'Next')}
          </MuiButton>
        )}

        {(isEdit || isLastTab) && (
          <SaveButton
            label={translate('ra.action.save', 'Save')}
            alwaysEnable
            transform={handleSave}
            type="button"
          />
        )}

        {!isEdit && (
          <MuiButton
            variant="outlined"
            color="error"
            onClick={() => {
              if (
                window.confirm(
                  translate('form.reset_confirm', 'Are you sure you want to reset the form?'),
                )
              ) {
                window.location.replace('/#/events-v2/create');
                window.location.reload();
              }
            }}
          >
            {translate('ra.action.reset', 'Reset')}
          </MuiButton>
        )}
      </Stack>
    </Toolbar>
  );
};
